import { SelectDropDown } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../../../_augmentt/selectors/customers'
import { createHubTask, pollTaskStatus } from '../../../../../crud/integrationHub.crud'
import useAllEmployees from '../../../../../hooks/useAllEmployees'
import useFreshEmployeeData from '../../../../../hooks/useFreshEmployeeData'
import { useValidate } from '../../../../../hooks/useValidate'
import Spinner from '../../../../../partials/content/Spinner'
import { TASK_ACTIONS, TASK_TYPES } from '../../../../../util/hub-task-management/hub.constants'
import { getEmployeeFullName } from '../../../../../util/table.util'

const propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onUpdate: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  employees: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  allEmployees: PropTypes.array,
}

function ManageEmailForwarding(props) {
  const {
    show = false,
    onHide = () => {},
    onUpdate = () => {},
    employees = [],
    allEmployees: propAllEmployees = [],
  } = props ?? {}

  const currentUser = useSelector(({ user }) => user.data)
  const selectedCustomer = useSelector(getSelectedCustomer)
  const selectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)

  // Use centralized hook for employee fetching
  const { allEmployees } = useAllEmployees({
    allEmployees: propAllEmployees,
    selectedCustomer,
    show,
  })

  const STATUS = {
    REVIEW: 'REVIEW',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
  }

  const ERRORS = {
    GLOBAL_ERRORS: 'GLOBAL_ERRORS',
    OPERATIONAL: 'OPERATIONAL',
  }

  const [taskErrors, setTaskErrors] = useState({})
  const [status, setStatus] = useState(STATUS.REVIEW)

  // Fetch fresh employee data when opened from table
  // Always refetch when modal opens to get latest email forwarding settings after saves
  const freshEmployees = useFreshEmployeeData({
    employee: employees,
    show,
    customer: selectedCustomer,
    isStaleData: (emp) => !(emp?.isUserMailBoxAvailable || emp?.isUserSharedMailbox),
  })

  const initialValues = useMemo(
    () => ({
      isForwardAll: false,
      forwardingSmtpAddress: null,
      deliverToMailboxAndForward: false,
    }),
    [],
  )

  const { forwardingSmtpAddress, deliverToMailboxAndForward } =
    freshEmployees?.length > 1 ? initialValues : freshEmployees?.[0] || initialValues

  // Check if employees have mailbox availability (same check as button enablement)
  const hasRequiredFields =
    freshEmployees?.length > 0 && freshEmployees.some((emp) => emp?.isUserMailBoxAvailable || emp?.isUserSharedMailbox)

  const $validate = useValidate(
    {
      isForwardAll: !!forwardingSmtpAddress || false,
      forwardingSmtpAddress: forwardingSmtpAddress || null,
      deliverToMailboxAndForward: deliverToMailboxAndForward || false,
    },
    {
      forwardingSmtpAddress: (value) => (!value ? 'Forwarding SMTP address is required' : null),
      deliverToMailboxAndForward: (value) =>
        [null, undefined].includes(value) ? 'Deliver to mailbox and forward is required' : null,
    },
  )

  const { values, handleChange, isValid, isDirty, hasAnyFieldChanged } = useMemo(() => $validate || {}, [$validate])

  const employeeEmailOptions = useMemo(
    () =>
      allEmployees
        .filter((item) => !item.ignored)
        .map((item) => ({ label: `${getEmployeeFullName(item)} (${item.email})`, value: item.email }))
        .sort((a, b) => a?.label?.localeCompare(b?.label)),
    [allEmployees],
  )

  const onApply = async () => {
    setStatus(STATUS.IN_PROGRESS)

    const { isForwardAll: _isForwardAll, ...emailForwarding } = values

    const parameters = {
      actions: {
        employeeIdentifiers: freshEmployees
          ?.filter((employee) => employee?.isUserMailBoxAvailable || employee?.isUserSharedMailbox)
          .map((emp) => emp.employeeIdentifier),
        emailForwarding: {
          ...(values.isForwardAll
            ? emailForwarding
            : { forwardingSmtpAddress: null, deliverToMailboxAndForward: false }),
        },
      },
      tasktype: TASK_TYPES.manageEmailForwarding,
      action: TASK_ACTIONS.update,
      customerId: selectedCustomer?.id,
      userId: currentUser?.id,
      organizationId: selectedCustomer?.organization,
    }

    try {
      const { data: { taskId } = {} } = await createHubTask(parameters)

      if (taskId) {
        const { data: { _source: task } = {} } =
          (await pollTaskStatus(taskId, {
            retries: 10,
            timeout: 3000,
          })) ?? {}

        setTaskErrors(JSON.parse(task?.errors || '{}'))
        setStatus(STATUS.COMPLETED)

        // Call onUpdate to refresh parent modal data
        if (onUpdate) {
          await onUpdate()
        }
      }
    } catch (error) {
      setTaskErrors({
        OPERATIONAL: { message: error.message },
      })
      setStatus(STATUS.COMPLETED)
    }
  }

  useEffect(() => {
    if (forwardingSmtpAddress) {
      handleChange({ isForwardAll: true })
    }

    $validate.validate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal className={`right ${clsx({ in: show })}`} show={show} onHide={onHide} size="lg" backdrop>
      <Modal.Header className="grey-bg-color-1" closeButton onHide={onHide}>
        <Modal.Title>
          <FormattedMessage id="ENGAGE.MANAGE_EMAIL_FORWARDING" />
        </Modal.Title>
        {freshEmployees?.length === 1 && (
          <p className="mt-2">
            {freshEmployees?.[0]?.firstName} {freshEmployees?.[0]?.lastName}
          </p>
        )}

        <p className="mt-2">
          {selectedIntegration?.IntegrationApplication?.display_name === 'M365'
            ? 'Microsoft 365'
            : selectedIntegration?.IntegrationApplication?.display_name}
        </p>
      </Modal.Header>

      <Modal.Body className="modal-body-sidebar">
        {[STATUS.REVIEW].includes(status) && (
          <>
            {hasRequiredFields && (
              <>
                {freshEmployees?.length > 1 && (
                  <Row className="px-2">
                    <Col md="12">
                      <Alert variant="secondary" className="mt-2">
                        <p className="font-bolder alert-text">
                          <FormattedMessage id="ENGAGE.MANAGE_EMAIL_FORWARDING_BULK_INFO" />
                        </p>
                      </Alert>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col>
                    <div className="text-color-default">
                      <Form.Check id="forward-all-checkbox-container">
                        <Form.Check.Input
                          type="checkbox"
                          name="forward-all-checkbox"
                          id="forward-all-checkbox"
                          checked={values.isForwardAll}
                          onChange={(e) => {
                            const { checked } = e.target
                            if (!checked) {
                              handleChange({ forwardingSmtpAddress: null })
                              handleChange({ deliverToMailboxAndForward: false })
                            }
                            handleChange({ isForwardAll: checked })
                          }}
                        />

                        <Form.Check.Label
                          className="fw-bold"
                          htmlFor="forward-all-checkbox"
                          style={{ marginLeft: '1.5rem' }}
                        >
                          <FormattedMessage id="ENGAGE.MANAGE_EMAIL_FORWARDING_FORWARD_ALL_EMAILS" />
                        </Form.Check.Label>
                      </Form.Check>
                    </div>
                  </Col>
                </Row>

                <Row className="px-4 mb-2">
                  <Col>
                    <p>
                      <FormattedMessage id="ENGAGE.MANAGE_EMAIL_FORWARDING_FORWARD_ALL_EMAILS_DESC" />
                    </p>
                  </Col>
                </Row>

                <Row className="px-4 mb-4">
                  <Col sm="10" md="10" lg="10">
                    <p className="mb-2">
                      <FormattedMessage id="ENGAGE.MANAGE_EMAIL_FORWARDING_FORWARD_EMAILS_TO" />
                    </p>
                    <SelectDropDown
                      id="forwarding-smtp-address-dropdown"
                      isDisabled={!values.isForwardAll}
                      isClearable={false}
                      value={
                        employeeEmailOptions.find(
                          (option) => option.value?.toLowerCase() === values.forwardingSmtpAddress?.toLowerCase(),
                        ) || null
                      }
                      options={employeeEmailOptions}
                      onChange={(option) => {
                        if (option) {
                          handleChange({ forwardingSmtpAddress: option.value })
                        }
                      }}
                    />
                  </Col>
                </Row>

                <Row className="px-4 mb-2">
                  <Col>
                    <div className="text-color-default">
                      <Form.Check
                        className="px-1 mb-3"
                        id="deliver-to-mailbox-checkbox-container"
                        style={{ zIndex: 0 }}
                      >
                        <Form.Check.Input
                          disabled={!values.isForwardAll}
                          type="checkbox"
                          name="deliver-to-mailbox-checkbox"
                          id="deliver-to-mailbox-checkbox"
                          checked={values.deliverToMailboxAndForward}
                          onChange={(e) => {
                            const value = e.target.checked
                            handleChange({ deliverToMailboxAndForward: value })
                          }}
                        />

                        <Form.Check.Label htmlFor="deliver-to-mailbox-checkbox" style={{ marginLeft: '1.5rem' }}>
                          <FormattedMessage id="ENGAGE.MANAGE_EMAIL_FORWARDING_FORWARD_KEEP_A_COPY" />
                        </Form.Check.Label>
                      </Form.Check>
                    </div>
                  </Col>
                </Row>

                {freshEmployees?.length > 1 && (
                  <>
                    <Row>
                      <Col md="12">
                        <p className="font-bolder mb-1 mt-4">
                          <FormattedMessage
                            id="ENGAGE.MANAGE_EMAIL_FORWARDING_APPLY_PHRASE"
                            values={{ s: freshEmployees?.length > 1 ? 's' : '' }}
                          />
                        </p>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="12">
                        <ul className="font-bolder">
                          {freshEmployees
                            ?.filter((employee) => employee?.isUserMailBoxAvailable || employee?.isUserSharedMailbox)
                            .map((employee) => (
                              <li key={employee.id}>
                                <Row>
                                  <Col md="10">
                                    <span>{getEmployeeFullName(employee)}</span>
                                  </Col>
                                </Row>
                              </li>
                            ))}
                        </ul>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}
            {!hasRequiredFields && (
              <Row className="px-2">
                <Col md="12">
                  <Alert variant="secondary" className="mt-2">
                    <p className="font-bolder alert-text">
                      <FormattedMessage id="ENGAGE.MANAGE_EMAIL_FORWARDING_NO_REQUIRED_FIELD" />
                    </p>
                  </Alert>
                </Col>
              </Row>
            )}
          </>
        )}

        {[STATUS.IN_PROGRESS].includes(status) && (
          <div className="modal-loading-centered">
            <Spinner size="sm" />
            <div className="mt-4 text-strong text-color-secondary">
              <FormattedMessage id="ENGAGE.MANAGE_EMAIL_FORWARDING_IN_PROGRESS" />
            </div>
          </div>
        )}

        {!taskErrors[ERRORS.GLOBAL_ERRORS] &&
          !taskErrors[ERRORS.OPERATIONAL] &&
          [STATUS.COMPLETED].includes(status) && (
            <ul className="ps-0">
              {freshEmployees?.map((employee) => {
                const hasError = !!taskErrors[employee?.employeeIdentifier]

                return (
                  <li key={employee.id} className="list-unstyled mt-0">
                    {hasError ? (
                      <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
                    ) : (
                      <i className="icon-xl align-text-bottom font-color-engage fas fa-circle-check me-2" />
                    )}
                    {getEmployeeFullName(employee)}

                    {hasError && (
                      <div style={{ width: '100%' }} className="font-color-danger">
                        <pre
                          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '14px' }}
                          className="font-color-danger"
                        >
                          {JSON.stringify(taskErrors[employee?.employeeIdentifier] || {}, null, 2)}
                        </pre>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}

        {taskErrors[ERRORS.OPERATIONAL] && [STATUS.COMPLETED].includes(status) && (
          <div className="font-color-danger mt-1 ps-0">
            <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
            <FormattedMessage id="GENERAL.ERROR" />
          </div>
        )}

        {taskErrors[ERRORS.GLOBAL_ERRORS] && [STATUS.COMPLETED].includes(status) && (
          <div className="font-color-danger mt-1 ps-0">
            <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
            <FormattedMessage id="GENERAL.ERROR" />
            <pre
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '14px' }}
              className="font-color-danger mt-1"
            >
              {JSON.stringify(taskErrors[ERRORS.GLOBAL_ERRORS] || {}, null, 2)}
            </pre>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className={clsx('d-flex', 'justify-content-between')}>
        <Button onClick={onHide} variant="secondary">
          <FormattedMessage id={[STATUS.REVIEW].includes(status) ? 'GENERAL.CANCEL' : 'GENERAL.CLOSE'} />
        </Button>
        {[STATUS.REVIEW].includes(status) && (
          <Button
            id="manage-email-forwarding-submit-btn"
            variant="primary"
            onClick={onApply}
            className="bg-color-engage pull-right"
            disabled={(() => {
              if (!hasRequiredFields) return true
              if (values.isForwardAll) return !isValid || !hasAnyFieldChanged || !isDirty
              return freshEmployees?.length === 0 ? !forwardingSmtpAddress : false
            })()}
          >
            <FormattedMessage id="GENERAL.APPLY" />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

ManageEmailForwarding.propTypes = propTypes

export default injectIntl(ManageEmailForwarding)
